import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';

import { WealthMilestoneChart } from '../charts/WealthMilestoneChart';
import { formatCurrency } from '../charts/d3/format';
import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import type { MainTabParamList } from '../../core/navigation/types';
import { colors, spacing, typography } from '../../core/theme/tokens';
import type { InvestmentCompounding } from '../../hooks/useInvestmentCompounding';
import {
  DEFAULT_ANNUAL_RATE_PCT,
  MAX_ANNUAL_RATE_PCT,
  MIN_ANNUAL_RATE_PCT,
} from '../../hooks/useInvestmentCompounding';
import {
  formatMilestoneYears,
  formatWealthAmount,
} from '../../utils/investmentCompounding';

type CompoundingExplorerProps = {
  model: InvestmentCompounding;
  /** When true, show editable principal / contribution. */
  showInputs?: boolean;
};

export function CompoundingExplorer({ model, showInputs = true }: CompoundingExplorerProps) {
  const navigation = useNavigation<BottomTabNavigationProp<MainTabParamList>>();
  const {
    principal,
    annualRatePct,
    years,
    tableRows,
    annualContribution,
    milestonePlan,
    targetAmount,
    setTargetAmount,
    nudgeTargetAmount,
    nudgePrincipal,
    setPrincipal,
    nudgeAnnualContribution,
    setAnnualContribution,
    nudgeAnnualRatePct,
    setAnnualRatePct,
    resetToPortfolio,
    seedPrincipal,
    seedAnnualContribution,
    seedExpectedRatePct,
  } = model;

  const endBalance = tableRows[tableRows.length - 1]?.endBalance ?? principal;

  if (seedPrincipal <= 0 && principal <= 0 && annualContribution <= 0) {
    return null;
  }

  return (
    <GlassCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>Time to {formatWealthAmount(targetAmount)}</Text>
          <Text style={styles.hint}>
            How long each {formatWealthAmount(milestonePlan.step)} takes at your assumed return
          </Text>
        </View>
        <View style={styles.headerActions}>
          <RefreshButton compact />
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={() => navigation.navigate('Grow')}
            accessibilityLabel="Open Grow tab"
          >
            Open Grow
          </GlassButton>
        </View>
      </View>

      {showInputs && (
        <>
          <View style={styles.inputRow}>
            <Text style={styles.inputLabel}>Starting investment</Text>
            <View style={styles.stepper}>
              <GlassButton
                variant="secondary"
                size="sm"
                onPress={() => nudgePrincipal(-5000)}
                accessibilityLabel="Decrease investment"
              >
                −
              </GlassButton>
              <GlassButton
                variant="ghost"
                size="sm"
                onPress={() => setPrincipal(seedPrincipal)}
                accessibilityLabel="Investment amount"
              >
                {formatCurrency(principal)}
              </GlassButton>
              <GlassButton
                variant="secondary"
                size="sm"
                onPress={() => nudgePrincipal(5000)}
                accessibilityLabel="Increase investment"
              >
                +
              </GlassButton>
            </View>
          </View>
          <View style={styles.inputRow}>
            <Text style={styles.inputLabel}>Yearly contribution</Text>
            <View style={styles.stepper}>
              <GlassButton
                variant="secondary"
                size="sm"
                onPress={() => nudgeAnnualContribution(-1000)}
                accessibilityLabel="Decrease contribution"
              >
                −
              </GlassButton>
              <GlassButton
                variant="ghost"
                size="sm"
                onPress={() => setAnnualContribution(seedAnnualContribution)}
                accessibilityLabel="Annual contribution"
              >
                {formatCurrency(annualContribution)}
              </GlassButton>
              <GlassButton
                variant="secondary"
                size="sm"
                onPress={() => nudgeAnnualContribution(1000)}
                accessibilityLabel="Increase contribution"
              >
                +
              </GlassButton>
            </View>
          </View>
          <View style={styles.inputRow}>
            <Text style={styles.inputLabel}>Expected return</Text>
            <View style={styles.stepper}>
              <GlassButton
                variant="secondary"
                size="sm"
                onPress={() => nudgeAnnualRatePct(-0.5)}
                accessibilityLabel="Decrease return"
              >
                −
              </GlassButton>
              <GlassButton
                variant="ghost"
                size="sm"
                onPress={() => setAnnualRatePct(seedExpectedRatePct)}
                accessibilityLabel="Expected return"
              >
                {`${annualRatePct.toFixed(1)}%`}
              </GlassButton>
              <GlassButton
                variant="secondary"
                size="sm"
                onPress={() => nudgeAnnualRatePct(0.5)}
                accessibilityLabel="Increase return"
              >
                +
              </GlassButton>
            </View>
          </View>
          <Text style={styles.rateHint}>
            Default {DEFAULT_ANNUAL_RATE_PCT}% (adjustable {MIN_ANNUAL_RATE_PCT}–
            {MAX_ANNUAL_RATE_PCT}%). Portfolio suggests {seedExpectedRatePct.toFixed(1)}%.
          </Text>
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={resetToPortfolio}
            accessibilityLabel="Reset to portfolio amounts"
          >
            Use portfolio amounts & return
          </GlassButton>
        </>
      )}

      <View style={styles.kpiRow}>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Return</Text>
          <Text style={styles.kpiValue}>{annualRatePct.toFixed(1)}%</Text>
        </View>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>
            {milestonePlan.reached && milestonePlan.totalYears != null
              ? `To ${formatWealthAmount(targetAmount)}`
              : `In ${years} years`}
          </Text>
          <Text style={[styles.kpiValue, styles.accent]}>
            {milestonePlan.reached && milestonePlan.totalYears != null
              ? `${formatMilestoneYears(milestonePlan.totalYears)}y`
              : formatCurrency(endBalance)}
          </Text>
        </View>
      </View>

      <View style={styles.inputRow}>
        <Text style={styles.inputLabel}>Target</Text>
        <View style={styles.stepper}>
          <GlassButton
            variant="secondary"
            size="sm"
            onPress={() => nudgeTargetAmount(-50_000)}
            accessibilityLabel="Decrease target"
          >
            −
          </GlassButton>
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={() => setTargetAmount(targetAmount)}
            accessibilityLabel="Target amount"
          >
            {formatWealthAmount(targetAmount)}
          </GlassButton>
          <GlassButton
            variant="secondary"
            size="sm"
            onPress={() => nudgeTargetAmount(50_000)}
            accessibilityLabel="Increase target"
          >
            +
          </GlassButton>
        </View>
      </View>
      <View style={styles.chipRow}>
        {[500_000, 1_000_000, 2_000_000].map((amount) => (
          <GlassButton
            key={amount}
            variant="secondary"
            size="sm"
            selected={targetAmount === amount}
            onPress={() => setTargetAmount(amount)}
          >
            {formatWealthAmount(amount)}
          </GlassButton>
        ))}
      </View>

      <WealthMilestoneChart
        plan={milestonePlan}
        annualRatePct={annualRatePct}
        annualContribution={annualContribution}
        height={220}
      />
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  inputLabel: {
    ...typography.body,
    color: colors.text,
    flex: 1,
    fontSize: 15,
  },
  rateHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  kpiRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  kpi: {
    flexGrow: 1,
    flexBasis: '40%',
    minWidth: 100,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    padding: spacing.sm,
    gap: 4,
  },
  kpiLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    fontSize: 10,
  },
  kpiValue: {
    ...typography.headline,
    color: colors.text,
    fontSize: 18,
    fontVariant: ['tabular-nums'],
  },
  accent: {
    color: colors.accent,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
});
